import { useState, useEffect } from 'react';
import { format, addMonths, subMonths, addWeeks, subWeeks, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay, addDays, getHours, getMinutes, isBefore, setHours, setMinutes } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './appointments-calendar.css';
import { AppointmentEvent, PatientAppointmentCalendarProps } from './PatientAppointmentCalendar.d';

// Constants
const HOURS = Array.from({ length: 12 }, (_, i) => i + 8); // 8 AM to 7 PM
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface StatusOption {
  label: string;
  bgClass: string;
}

// Status options for legend
const statusOptions: Record<AppointmentEvent['status'], StatusOption> = {
  confirmed: { label: 'Confirmed', bgClass: 'confirmed' },
  scheduled: { label: 'Scheduled', bgClass: 'scheduled' },
  completed: { label: 'Completed', bgClass: 'completed' },
  cancelled: { label: 'Cancelled', bgClass: 'cancelled' },
  rescheduled: { label: 'Rescheduled', bgClass: 'rescheduled' }
};

// Enhanced appointment with calculated position
interface EnhancedAppointment extends AppointmentEvent {
  column?: number;
  span?: number;
  totalColumns?: number;
}

const PatientAppointmentCalendar: React.FC<PatientAppointmentCalendarProps> = ({
  appointments = [],
  currentDate,
  view = 'month',
  onViewChange,
  onDateChange,
  onTimeSlotClick,
  onSelectEvent: _onSelectEvent = () => {},
  onAppointmentClick = () => {},
  isDark,
  isLoading = false
}) => {
  // Calculate calendar dates
  const [calendarDates, setCalendarDates] = useState<Date[]>([]);
  
  // Effect to calculate dates when current date or view changes
  useEffect(() => {
    if (view === 'month') {
      const startDate = startOfWeek(startOfMonth(currentDate));
      const endDate = endOfWeek(endOfMonth(currentDate));
      let day = startDate;
      const days: Date[] = [];
      
      while (isBefore(day, endDate) || isSameDay(day, endDate)) {
        days.push(day);
        day = addDays(day, 1);
      }
      
      setCalendarDates(days);
    } else if (view === 'week') {
      const startDate = startOfWeek(currentDate);
      const days = Array.from({ length: 7 }, (_, i) => addDays(startDate, i));
      setCalendarDates(days);
    }
  }, [currentDate, view]);
  
  // Navigation functions
  const navigateToToday = () => {
    onDateChange?.(new Date());
  };
  
  const navigateToPrevious = () => {
    if (view === 'month') {
      onDateChange?.(subMonths(currentDate, 1));
    } else if (view === 'week') {
      onDateChange?.(subWeeks(currentDate, 1));
    }
  };
  
  const navigateToNext = () => {
    if (view === 'month') {
      onDateChange?.(addMonths(currentDate, 1));
    } else if (view === 'week') {
      onDateChange?.(addWeeks(currentDate, 1));
    }
  };
  
  // Helper functions
  const getAppointmentsForDay = (day: Date): AppointmentEvent[] => {
    return appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.start);
      return isSameDay(appointmentDate, day);
    });
  };
  
  const getAppointmentsForHour = (day: Date, hour: number): AppointmentEvent[] => {
    return appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.start);
      return (
        isSameDay(appointmentDate, day) &&
        getHours(appointmentDate) === hour
      );
    });
  };
  
  // Calculate appointment style for week view
  const calculateAppointmentStyle = (appointment: AppointmentEvent): React.CSSProperties => {
    const start = new Date(appointment.start);
    const end = new Date(appointment.end);
    
    const startHour = getHours(start);
    const startMinute = getMinutes(start);
    const endHour = getHours(end);
    const endMinute = getMinutes(end);
    
    // Convert to position within the grid
    // Each hour is 4rem tall, and we're showing 8AM (index 0) to 7PM (index 11)
    const startPosition = (startHour - 8) * 4 + (startMinute / 60) * 4;
    let height = (endHour - startHour) * 4 + ((endMinute - startMinute) / 60) * 4;
    
    // Minimum height for visibility
    height = Math.max(height, 1);
    
    return {
      top: `${startPosition}rem`,
      height: `${height}rem`
    };
  };
  
  // Group overlapping appointments
  const groupAppointments = (appointments: AppointmentEvent[], _day: Date, _hour: number): EnhancedAppointment[] => {
    if (appointments.length <= 1) return appointments.map(appt => ({ ...appt, column: 0, span: 1 }));
    
    // Sort by start time
    const sorted = [...appointments].sort((a, b) => 
      new Date(a.start).getTime() - new Date(b.start).getTime()
    );
    
    // Track columns and assign
    const columns: { end: number }[] = [];
    const result: EnhancedAppointment[] = [];
    
    for (const appt of sorted) {
      // Find first available column
      let column = 0;
      while (columns[column]?.end > new Date(appt.start).getTime()) {
        column++;
      }
      
      // Assign column
      columns[column] = { end: new Date(appt.end).getTime() };
      result.push({ ...appt, column, span: 1 });
    }
    
    // Calculate total columns and spans
    const totalColumns = columns.length;
    return result.map(appt => ({ ...appt, totalColumns, span: 1 }));
  };
  
  // Render month view
  const renderMonthView = () => {
    return (
      <div className="month-view">
        {/* Day headers */}
        {SHORT_DAYS.map((day, index) => (
          <div key={`header-${index}`} className="day-header">
            {day}
          </div>
        ))}
        
        {/* Day cells */}
        {calendarDates.map((date, index) => {
          const dayAppointments = getAppointmentsForDay(date);
          const isToday = isSameDay(date, new Date());
          const isCurrentMonth = isSameMonth(date, currentDate);
          
          // Show max 3 appointments per day in month view
          const visibleAppointments = dayAppointments.slice(0, 3);
          const hasMoreAppointments = dayAppointments.length > 3;
          
          return (
            <div 
              key={`day-${index}`} 
              className={`day-cell ${isToday ? 'day-today' : ''} ${!isCurrentMonth ? 'day-outside-month' : ''}`}
              onClick={() => onTimeSlotClick?.(setHours(setMinutes(date, 0), 9))} // Default to 9am
            >
              <span className="day-number">
                {format(date, 'd')}
              </span>
              
              {/* Appointments for the day */}
              {visibleAppointments.map((appointment, idx) => (
                <div 
                  key={`appt-${appointment.id || idx}`}
                  className={`month-appointment ${appointment.status}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAppointmentClick(appointment);
                  }}
                >
                  <span className="time-text">{format(new Date(appointment.start), 'h:mm a')}</span> {appointment.title}
                </div>
              ))}
              
              {/* More indicator */}
              {hasMoreAppointments && (
                <div className="appointment-count">
                  + {dayAppointments.length - 3} more
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };
  
  // Render week view
  const renderWeekView = () => {
    // Get the current time for the time indicator
    const now = new Date();
    const currentHour = getHours(now);
    const currentMinute = getMinutes(now);
    const showTimeIndicator = calendarDates.some(date => isSameDay(date, now));
    
    // Calculate position for current time indicator
    const timeIndicatorStyle: React.CSSProperties = {
      top: `${(currentHour - 8) * 4 + (currentMinute / 60) * 4}rem`
    };
    
    return (
      <div className="week-view">
        {/* Week header with day names */}
        <div className="week-header">
          <div className="time-label"></div> {/* Empty cell for time column */}
          {calendarDates.map((date, index) => {
            const isToday = isSameDay(date, new Date());
            return (
              <div key={`header-${index}`} className={`week-day-header ${isToday ? 'today-column' : ''}`}>
                <span className="week-day-name">{SHORT_DAYS[index]}</span>
                <span className="week-day-date">{format(date, 'd')}</span>
              </div>
            );
          })}
        </div>
        
        {/* Time grid */}
        <div className="time-grid">
          {/* Time labels column */}
          <div className="time-labels-column">
            {HOURS.map(hour => (
              <div key={`time-${hour}`} className="time-label">
                {hour % 12 === 0 ? '12' : hour % 12}:00 {hour >= 12 ? 'PM' : 'AM'}
              </div>
            ))}
          </div>
          
          {/* Days grid */}
          <div className="days-grid">
            {/* Current time indicator */}
            {showTimeIndicator && currentHour >= 8 && currentHour <= 19 && (
              <div className="current-time-indicator" style={timeIndicatorStyle}></div>
            )}
            
            {/* One column per day */}
            {calendarDates.map((date, dayIndex) => {
              const isToday = isSameDay(date, new Date());
              
              return (
                <div 
                  key={`day-column-${dayIndex}`} 
                  className={`day-column ${isToday ? 'today-column' : ''}`}
                >
                  {/* Hour cells for this day */}
                  {HOURS.map((hour, hourIndex) => {
                    const hourAppointments = getAppointmentsForHour(date, hour);
                    const groupedAppointments = groupAppointments(hourAppointments, date, hour);
                    const cellDate = new Date(date);
                    cellDate.setHours(hour, 0, 0, 0);
                    
                    return (
                      <div 
                        key={`hour-${dayIndex}-${hourIndex}`}
                        className="hour-cell"
                        onClick={() => onTimeSlotClick?.(cellDate)}
                      >
                        {/* Render appointments in this hour cell */}
                        {groupedAppointments.map((appointment, idx) => {
                          const style = calculateAppointmentStyle(appointment);
                          const columnWidth = appointment.totalColumns ? 100 / appointment.totalColumns : 100;
                          const leftPosition = appointment.column ? columnWidth * appointment.column : 0;
                          const widthValue = appointment.span && appointment.totalColumns
                            ? columnWidth * appointment.span
                            : columnWidth;
                            
                          const appointmentStyle: React.CSSProperties = {
                            ...style,
                            left: `${leftPosition}%`,
                            width: `${widthValue}%`,
                          };
                          
                          return (
                            <div 
                              key={`week-appt-${appointment.id || idx}`}
                              className={`week-appointment ${appointment.status}`}
                              style={appointmentStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                onAppointmentClick(appointment);
                              }}
                            >
                              <div className="appointment-title">{appointment.title}</div>
                              <div className="appointment-time">
                                {format(new Date(appointment.start), 'h:mm a')} - {format(new Date(appointment.end), 'h:mm a')}
                              </div>
                              <div className="appointment-doctor">{appointment.doctor}</div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };
  
  // Get the title for the calendar based on the current view and date
  const getCalendarTitle = (): string => {
    if (view === 'month') {
      return format(currentDate, 'MMMM yyyy');
    } else if (view === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      
      if (format(weekStart, 'MMM') === format(weekEnd, 'MMM')) {
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'd, yyyy')}`;
      } else if (format(weekStart, 'yyyy') === format(weekEnd, 'yyyy')) {
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      } else {
        return `${format(weekStart, 'MMM d, yyyy')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      }
    }
    return '';
  };
  
  return (
    <div className={`calendar-container ${isDark ? 'dark-theme' : ''} ${isLoading ? 'is-loading' : ''}`}>
      {/* Calendar header with navigation controls */}
      <div className="calendar-header">
        <div className="calendar-controls">
          <button 
            className="calendar-nav-button"
            onClick={navigateToPrevious}
            aria-label="Previous"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button 
            className="calendar-today-button"
            onClick={navigateToToday}
            aria-label="Today"
          >
            Today
          </button>
          <button 
            className="calendar-nav-button"
            onClick={navigateToNext}
            aria-label="Next"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        
        <h2 className="calendar-title">{getCalendarTitle()}</h2>
        
        <div className="calendar-view-selector">
          <button 
            className={`calendar-view-button ${view === 'month' ? 'active' : ''}`}
            onClick={() => onViewChange?.('month')}
            aria-pressed={view === 'month'}
            aria-label="Month view"
          >
            Month
          </button>
          <button 
            className={`calendar-view-button ${view === 'week' ? 'active' : ''}`}
            onClick={() => onViewChange?.('week')}
            aria-pressed={view === 'week'}
            aria-label="Week view"
          >
            Week
          </button>
        </div>
      </div>
      
      {/* Main calendar grid */}
      <div className="calendar-body">
        {view === 'month' ? renderMonthView() : renderWeekView()}
        
        {/* Loading overlay */}
        {isLoading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <span>Loading appointments...</span>
          </div>
        )}
      </div>
      
      {/* Status legend */}
      <div className="status-legend">
        <span className="legend-title">Status:</span>
        {Object.entries(statusOptions).map(([status, { label, bgClass }]) => (
          <span key={status} className="legend-item">
            <span className={`status-dot ${bgClass}`}></span>
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default PatientAppointmentCalendar; 